// src/scripts/calendar/config/CalendarConfig.js

/**
 * CalendarConfig
 * --------------
 * Centralized configuration management for the calendar system.
 *
 * RESPONSIBILITIES
 * - Provide defaults and merge user overrides
 * - Validate configuration (fail fast with explicit error messages)
 * - Provide safe read-only accessors for commonly-used config sections
 * - Offer a small set of convenience helpers (SSR placeholder, presets, etc.)
 *
 * ERROR HANDLING PHILOSOPHY
 * - ErrorHandler is TELEMETRY ONLY: it records errors/successes and keeps a bounded history.
 * - CalendarConfig is authoritative for correctness: it throws on invalid config.
 * - Do not use ErrorHandler as a “health oracle” (no “fatal” logic here).
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

// Environment detection for SSR compatibility
const isServer = typeof window === 'undefined';

// Telemetry-only error tracking
const errorHandler = new ErrorHandler({
  module: 'CalendarConfig',
  maxErrors: 10,
});

const UNSAFE_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

function isPlainObject(value) {
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

class CalendarConfig {
  constructor(options = {}) {
    try {
      // Default configuration
      this.defaults = {
        selectors: {
          calendar: '#calendar',
          calendarSection: '.calendar-calendar',
          eventTitle: '.fc-event-title',
          eventFrame: '.fc-event-main-frame',
          eventDate: '.fc-event-date',
        },
        classes: {
          hide: 'hide',
        },
        plugins: ['dayGrid'],
        debug: false,
        exposeGlobals: true,
        autoRender: true,
        errorRetryAttempts: 3,
        componentTimeout: 5000,
        performance: {
          enableMetrics: false,
          logSlowOperations: true,
          slowOperationThreshold: 100,
        },
        // Enhanced calendar-specific configurations
        calendar: {
          initialView: 'dayGridMonth',
          headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,dayGridWeek',
          },
          height: 'auto',
          eventDisplay: 'block',
          dayMaxEvents: 2,
          moreLinkClick: 'popover',
          eventOrder: 'start,-duration,allDay,title',
          weekends: true,
          nowIndicator: true,
          fixedWeekCount: false,
          showNonCurrentDates: false,
          businessHours: {
            daysOfWeek: [1, 2, 3, 4, 5], // Monday - Friday
            startTime: '09:00',
            endTime: '17:00',
          },
        },
        eventHandling: {
          enableTooltips: true,
          enableModal: true,
          enableClickHandlers: true,
          customClassPrefix: 'event-type-',
        },
        fallbackStrategy: {
          enabled: true,
          order: ['full', 'alternative', 'basic', 'minimal'],
          logFailures: true,
        },
        ssr: {
          enabled: isServer,
          renderPlaceholder: true,
          placeholderText: 'Calendar loading...',
          placeholderClass: 'calendar-placeholder',
        },
      };

      // Merge with provided options (safe deep merge)
      this.config = this._deepMerge(this.defaults, options);

      // Validate configuration (throws on invalid)
      this._validate();

      // Set up readonly properties
      this._setupReadonlyProperties();

      errorHandler.logSuccess('CalendarConfig initialized successfully', {
        category: 'initialization',
        action: 'config-init-success',
      });
    } catch (error) {
      // Telemetry only; constructor still throws so callers can fail fast.
      errorHandler.handleError('CalendarConfig initialization failed', error, {
        category: 'initialization',
        action: 'config-init-failed',
      });
      throw error;
    }
  }

  /**
   * Get configuration value by key (supports dot notation)
   * @param {string} key - Configuration key
   * @returns {*} Configuration value
   */
  get(key) {
    try {
      return this._getNestedValue(this.config, key);
    } catch (error) {
      errorHandler.handleError('Error getting configuration value', error, {
        category: 'configuration',
        action: 'config-get-failed',
        key,
      });
      return undefined;
    }
  }

  /**
   * Set configuration value by key (supports dot notation)
   * @param {string} key - Configuration key
   * @param {*} value - Configuration value
   */
  set(key, value) {
    try {
      this._setNestedValue(this.config, key, value);
      this._validate();
    } catch (error) {
      errorHandler.handleError('Error setting configuration value', error, {
        category: 'configuration',
        action: 'config-set-failed',
        key,
      });
    }
  }

  /**
   * Get all configuration as readonly object
   * @returns {Object} Configuration object
   */
  getAll() {
    return Object.freeze(JSON.parse(JSON.stringify(this.config)));
  }

  /**
   * Get calendar-specific configuration suitable for FullCalendar
   * @returns {Object} FullCalendar configuration object
   */
  getCalendarConfig() {
    return this.config.calendar;
  }

  /**
   * Get fallback strategy configuration
   * @returns {Object} Fallback strategy configuration
   */
  getFallbackStrategy() {
    return this.config.fallbackStrategy;
  }

  /**
   * Get event handling configuration
   * @returns {Object} Event handling configuration
   */
  getEventHandling() {
    return this.config.eventHandling;
  }

  /**
   * Get SSR configuration
   * @returns {Object} SSR configuration
   */
  getSSRConfig() {
    return this.config.ssr;
  }

  /**
   * Check if running in SSR mode
   * @returns {boolean} True if SSR mode
   */
  isSSR() {
    return this.config.ssr.enabled;
  }

  /**
   * Get environment-appropriate selector
   * @param {string} selectorName - Name of the selector
   * @returns {string|null} Selector string or null if SSR
   */
  getSelector(selectorName) {
    if (this.isSSR()) return null;
    return this.config.selectors[selectorName];
  }

  /**
   * Setup readonly property accessors.
   * Refactor: avoids repetitive Object.defineProperty calls.
   */
  _setupReadonlyProperties() {
    const readonlyKeys = [
      'selectors',
      'classes',
      'plugins',
      'debug',
      'exposeGlobals',
      'autoRender',
      'errorRetryAttempts',
      'componentTimeout',
      'calendar',
      'eventHandling',
      'fallbackStrategy',
      'ssr',
    ];

    for (const key of readonlyKeys) {
      Object.defineProperty(this, key, {
        get: () => this.config[key],
        enumerable: true,
      });
    }
  }

  /**
   * Enhanced validation with calendar-specific checks
   * @throws {Error} If configuration is invalid
   */
  _validate() {
    try {
      const errors = [];

      // Required fields
      if (!this.config.selectors?.calendar) {
        errors.push('Calendar selector is required');
      }

      // Type validations
      if (typeof this.config.debug !== 'boolean') errors.push('debug must be a boolean');
      if (typeof this.config.exposeGlobals !== 'boolean') errors.push('exposeGlobals must be a boolean');
      if (typeof this.config.autoRender !== 'boolean') errors.push('autoRender must be a boolean');

      if (!Number.isInteger(this.config.errorRetryAttempts) || this.config.errorRetryAttempts < 0) {
        errors.push('errorRetryAttempts must be a non-negative integer');
      }

      if (!Number.isInteger(this.config.componentTimeout) || this.config.componentTimeout < 0) {
        errors.push('componentTimeout must be a non-negative integer');
      }

      // Plugin validation
      if (!Array.isArray(this.config.plugins)) errors.push('plugins must be an array');

      // Selector validation
      if (!isPlainObject(this.config.selectors)) {
        errors.push('selectors must be an object');
      } else {
        for (const key of Object.keys(this.config.selectors)) {
          if (typeof this.config.selectors[key] !== 'string') {
            errors.push(`selector '${key}' must be a string`);
          }
        }
      }

      // Calendar configuration validation
      if (!isPlainObject(this.config.calendar)) {
        errors.push('calendar configuration must be an object');
      } else {
        const validViews = ['dayGridMonth', 'dayGridWeek', 'dayGridDay', 'listWeek', 'listDay'];
        if (!validViews.includes(this.config.calendar.initialView)) {
          errors.push(`calendar.initialView must be one of: ${validViews.join(', ')}`);
        }

        if (
          this.config.calendar.dayMaxEvents !== undefined &&
          (!Number.isInteger(this.config.calendar.dayMaxEvents) || this.config.calendar.dayMaxEvents < 1)
        ) {
          errors.push('calendar.dayMaxEvents must be a positive integer');
        }
      }

      // Event handling validation
      if (!isPlainObject(this.config.eventHandling)) {
        errors.push('eventHandling configuration must be an object');
      } else {
        const booleanProps = ['enableTooltips', 'enableModal', 'enableClickHandlers'];
        for (const prop of booleanProps) {
          if (this.config.eventHandling[prop] !== undefined && typeof this.config.eventHandling[prop] !== 'boolean') {
            errors.push(`eventHandling.${prop} must be a boolean`);
          }
        }
      }

      // Fallback strategy validation
      if (!isPlainObject(this.config.fallbackStrategy)) {
        errors.push('fallbackStrategy configuration must be an object');
      } else {
        if (!Array.isArray(this.config.fallbackStrategy.order)) {
          errors.push('fallbackStrategy.order must be an array');
        }
      }

      // SSR validation
      if (!isPlainObject(this.config.ssr)) {
        errors.push('ssr configuration must be an object');
      }

      if (errors.length > 0) {
        const errorMessage = `Configuration validation failed: ${errors.join(', ')}`;

        // Telemetry only (do not treat as “fatal system state”)
        errorHandler.handleError('Configuration validation failed', new Error(errorMessage), {
          category: 'configuration',
          action: 'validation-failed',
          errorCount: errors.length,
        });

        throw new Error(errorMessage);
      }
    } catch (error) {
      // Preserve existing behavior: validation failures throw.
      if (String(error?.message || '').includes('Configuration validation failed')) throw error;

      errorHandler.handleError('Unexpected error during configuration validation', error, {
        category: 'configuration',
        action: 'validation-error',
      });
      throw error;
    }
  }

  /**
   * Deep merge two objects (safe)
   * - Only merges plain objects
   * - Overwrites arrays/primitives
   * - Skips unsafe keys to avoid prototype pollution
   */
  _deepMerge(target, source) {
    const result = Object.create(null);

    // Copy target props
    if (isPlainObject(target)) {
      for (const key of Object.keys(target)) {
        if (UNSAFE_KEYS.has(key)) continue;
        result[key] = target[key];
      }
    }

    // Merge source props
    if (isPlainObject(source)) {
      for (const key of Object.keys(source)) {
        if (UNSAFE_KEYS.has(key)) continue;

        const srcVal = source[key];
        const tgtVal = isPlainObject(target) ? target[key] : undefined;

        if (isPlainObject(srcVal)) {
          result[key] = this._deepMerge(isPlainObject(tgtVal) ? tgtVal : {}, srcVal);
        } else {
          // arrays/primitives overwrite by design
          result[key] = srcVal;
        }
      }
    }

    // Normalize back to a plain object so JSON/stringify/freeze behave as expected
    return Object.assign({}, result);
  }

  /**
   * Get nested value from object using dot notation
   * @param {Object} obj - Object to search
   * @param {string} key - Key path (e.g., 'selectors.calendar')
   * @returns {*} Value or undefined
   */
  _getNestedValue(obj, key) {
    const keys = key.split('.');
    let current = obj;

    for (const keyPart of keys) {
      if (current && current[keyPart] !== undefined) {
        current = current[keyPart];
      } else {
        return undefined;
      }
    }

    return current;
  }

  /**
   * Set nested value in object using dot notation
   * @param {Object} obj - Object to modify
   * @param {string} key - Key path (e.g., 'selectors.calendar')
   * @param {*} value - Value to set
   */
  _setNestedValue(obj, key, value) {
    const keys = key.split('.');
    const lastKey = keys.pop();
    let target = obj;

    for (const prop of keys) {
      if (UNSAFE_KEYS.has(prop)) return; // refuse unsafe path
      if (!target[prop] || typeof target[prop] !== 'object') {
        target[prop] = {};
      }
      target = target[prop];
    }

    if (!UNSAFE_KEYS.has(lastKey)) {
      target[lastKey] = value;
    }
  }

  /**
   * Create a calendar preset configuration
   * @param {string} presetName - Name of the preset
   * @param {Object} customizations - Custom overrides for the preset
   * @returns {Object} Calendar configuration
   */
  createCalendarPreset(presetName, customizations = {}) {
    const baseConfig = this.getCalendarConfig();

    const presets = {
      minimal: {
        initialView: 'listWeek',
        headerToolbar: { left: 'prev,next', center: 'title', right: '' },
        height: 'auto',
      },
      basic: {
        initialView: 'listWeek',
        headerToolbar: { left: 'prev,next today', center: 'title', right: 'listWeek' },
        height: 'auto',
      },
      alternative: {
        initialView: 'dayGridMonth',
        headerToolbar: { left: 'prev,next today', center: 'title', right: 'dayGridMonth' },
        height: 'auto',
      },
      full: baseConfig,
    };

    const preset = presets[presetName] || presets.full;
    return this._deepMerge(preset, customizations);
  }

  /**
   * Get configuration for specific environment
   * @param {string} environment - 'server' or 'client'
   * @returns {Object} Environment-specific configuration
   */
  getEnvironmentConfig(environment) {
    const config = this.getAll();

    if (environment === 'server') {
      return {
        ssr: config.ssr,
        debug: config.debug,
        performance: config.performance,
        fallbackStrategy: config.fallbackStrategy,
      };
    }

    return config;
  }

  /**
   * Validate calendar element exists and is valid
   * @param {string|Element} element - Selector string or DOM element
   * @returns {Element|null} Valid DOM element or null
   */
  validateCalendarElement(element) {
    if (this.isSSR()) return null;

    if (typeof element === 'string') {
      try {
        return document.querySelector(element);
      } catch (e) {
        console.error('Invalid selector:', element);
        return null;
      }
    }

    if (element && element.nodeType === 1) return element;
    return null;
  }

  /**
   * Generate SSR placeholder HTML
   * @returns {string} HTML string for SSR placeholder
   */
  generateSSRPlaceholder() {
    if (!this.config.ssr.renderPlaceholder) return '';

    const cssClass = this.config.ssr.placeholderClass;
    const text = this.config.ssr.placeholderText;

    return `<div class="${cssClass}">${text}</div>`;
  }

  /**
   * Log configuration state for debugging
   */
  logConfiguration() {
    if (!this.config.debug) return;

    console.group('📅 CalendarConfig State');
    console.log('Environment:', this.isSSR() ? 'Server' : 'Client');
    console.log('Auto Render:', this.config.autoRender);
    console.log('Calendar View:', this.config.calendar.initialView);
    console.log('Fallback Strategy:', this.config.fallbackStrategy.enabled);
    console.log('Event Handling:', this.config.eventHandling);
    console.groupEnd();
  }

  /**
   * Get recent errors for debugging
   * NOTE: ErrorHandler.getRecentErrors takes a COUNT (limit), not minutes.
   * @param {number} limit - Number of errors to return (default: 5)
   * @returns {Array} Recent error entries
   */
  getRecentErrors(limit = 5) {
    return errorHandler.getRecentErrors(limit);
  }

  /**
   * Check if any errors have been recorded in telemetry.
   * NOTE: This is NOT “system health”; it’s simply “has any errors occurred?”
   * @returns {boolean}
   */
  hasCriticalIssues() {
    return errorHandler.hasCriticalIssues();
  }

  /**
   * Clear error history (useful for testing or after resolving issues)
   */
  clearErrorHistory() {
    errorHandler.clearErrorHistory();
  }

  /**
   * Get lightweight stats (built from the real ErrorHandler API).
   * @returns {Object}
   */
  getStats() {
    const totalErrors = errorHandler.getErrorCount();
    const recent = errorHandler.getRecentErrors(5).length;

    return {
      isSSR: this.isSSR(),
      configValid: !!this.config,
      hasSelectors: !!this.config.selectors,
      errors: {
        total: totalErrors,
        recent,
      },
    };
  }
}

export default CalendarConfig;
